<template>
  <component
    :is="isMobile ? CoreBottomSheet : CoreDialog"
    v-model="isOpened"
    title="Как работает клубная цена"
    v-bind="isMobile
      ? {}
      : {
        size: 'lg',
        divider: true,
      }"
  >
    <template
      v-if="!isMobile"
      #prependBtn="{ btnProps }"
    >
      <v-btn
        v-bind="btnProps"
        @click="isOpened = false"
      >
        <span class="ui-icon-close-not-a-circle" />
      </v-btn>
    </template>
    <div
      v-if="clubOfferData.cardDateEnd"
      class="text-secondary--text text-body-1 mb-6"
    >
      Вы в клубе до {{ clubOfferData.cardDateEnd }} 👑
    </div>

    <StepsToFollow class="mb-4" />

    <ClubInfo class="mb-5">
      <template #activator="{ on }">
        <span
          class="text-body-1 primary--text link-decoration-none cursor-pointer"
          v-on="on"
        >
          Подробнее о клубе
        </span>
      </template>
    </ClubInfo>

    <div class="price-details">
      <div class="price-details__inner pt-6 px-5 pb-5">
        <ClubPriceDetails
          :full-price="fullPrice"
          :club-offer-data="clubOfferData"
          :total-price="clubOfferData.totalPrice"
          :card-price="clubOfferData.cardPrice"
          :lpu-price="clubOfferData.lpuPrice"
          :option-price="clubOfferData.couponPrice"
          :is-no-card="!clubOfferData.cardDateEnd"
        />
      </div>
    </div>
    <template #actions>
      <v-btn
        data-qa="modal-actions-btn-close"
        class="bg-primary branding-btn branding-btn--background"
        color="primary"
        block
        text
        @click="isOpened = false"
      >
        Хорошо
      </v-btn>
    </template>
  </component>
</template>

<script lang="ts" setup>
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue'
import scrollLock from 'src/utils/scrollLock'
import useIsMobile from 'src/composables/useIsMobile'
import CoreDialog from 'src/core/CoreDialog/CoreDialog.vue'
import CoreBottomSheet from 'src/core/CoreBottomSheet/CoreBottomSheet.vue'
import ClubPriceDetails from 'src/components/common/ClubPriceInfo.vue/components/ClubPriceDetails.vue'
import StepsToFollow from 'src/components/common/ClubPriceInfo.vue/components/StepsToFollow.vue'
import ClubInfo from 'src/components/common/ClubInfo/ClubInfo.vue'

export type ClubPriceInfoOfferData = {
  lpuPrice: number | null
  couponPrice: number | null
  cardDateEnd: string | null
  cardPrice: number | null
  totalPrice: number | null
}

type ClubPriceInfoOpenPayload = {
  fullPrice?: number
  clubOfferData: ClubPriceInfoOfferData
}

type Emits = {
  (_event: 'input', _value: boolean): void
}

const EMPTY_OFFER: ClubPriceInfoOfferData = {
  lpuPrice: null,
  couponPrice: null,
  cardDateEnd: null,
  cardPrice: null,
  totalPrice: null,
}

const props = defineProps<{
  value?: boolean
  fullPrice?: number
  clubOfferData?: ClubPriceInfoOfferData
  /**
   * Не слушать `club-discount-info-bottom-sheet:open`.
   * Нужно списку врачей: там открытие через v-model, а не через window.
   */
  skipWindowOpen?: boolean
}>()
const emit = defineEmits<Emits>()

const isMobile = useIsMobile()
const internalOpened = ref(false)
const localFullPrice = ref(0)
const localOfferData = ref<ClubPriceInfoOfferData>({ ...EMPTY_OFFER })

const isOpened = computed({
  get: () => internalOpened.value,
  set: value => {
    internalOpened.value = value
    emit('input', value)
  },
})
const fullPrice = computed(() => props.fullPrice ?? localFullPrice.value)
const clubOfferData = computed(() => props.clubOfferData || localOfferData.value)

watch(() => props.value, isOpen => {
  internalOpened.value = Boolean(isOpen)
}, { immediate: true })

watch(isOpened, value => {
  if (process.env.IS_CLIENT) {
    scrollLock.set(value)
  }
})

function openFromEvent(payload: ClubPriceInfoOpenPayload) {
  localFullPrice.value = payload.fullPrice ?? 0
  localOfferData.value = payload.clubOfferData
  isOpened.value = true
}

function onLegacyOpen(event: Event) {
  const { detail } = event as CustomEvent<ClubPriceInfoOpenPayload>

  if (detail?.clubOfferData) {
    openFromEvent(detail)
  }
}

onMounted(() => {
  if (props.skipWindowOpen) {
    return
  }

  window.addEventListener('club-discount-info-bottom-sheet:open', onLegacyOpen)
})

onUnmounted(() => {
  if (props.skipWindowOpen) {
    return
  }

  window.removeEventListener('club-discount-info-bottom-sheet:open', onLegacyOpen)
})
</script>

<style lang="scss" scoped>
@import '~www/themes/doctors/common/variables';

.price-details {
  padding: 1px;
  border-radius: $border-radius-md;
  background-image: $club-coral-linear-gradient;

  &__inner {
    overflow: hidden;
    background-color: $ui-kit-bg-gray-0;
    border-radius: $border-radius-md;
  }
}
</style>
